import './polyfills'; // must load first

// Disabled: https://github.com/facebook/react/issues/29915
// import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { type Cache, SWRConfig, type SWRConfiguration } from 'swr';
import AppLoader from './App';

// for legacy build, index.css gets removed, legacy.css stays
import './index.css';
import './legacy.css';

// Legacy
import { type DriveFile } from '@/shared/DriveTypes';
import r2wc from '@r2wc/react-to-web-component';
import { dequal } from 'dequal/lite';
import { Route, Switch } from 'wouter';
import { LastModified } from './components/LastModified/LastModified';
import PrevNext from './components/PrevNext/PrevNext';
import { Search } from './components/Search/Search';
import { StaticTree } from './components/Tree/StaticTree';
import WikiSelector from './components/WikiSelector/WikiSelector';
import { Breadcrumbs } from './elements/Breadcrumbs/Breadcrumbs';
import Icon from './elements/Icon/Icon';
import PoweredBy from './elements/PoweredBy/PoweredBy';
import Title from './elements/Title/Title';

import { cache, cacheKey } from './cache';
import './elements/CacheImg';
import MuiThemeWrapper from './MuiTheme';
import { lightTheme } from './theme.css';
import { cacheFlag, clearCache, DEV, expiredToken, isIOS, LEGACY, MODERN } from './utils/constants';
import { type ExportResult } from './utils/DriveAPI';
import gup from './utils/gup';
import { log } from './utils/logger';
import { failRequests } from './utils/middleware/failRequests';
import type { Profile } from './utils/types';
import { get } from './utils/utils';
import * as wcStyles from './wc.css';

if (DEV) {
  await import('./utils/runtime-error-overlay');
  if (import.meta.hot) {
    import.meta.hot?.on('vite:beforeUpdate', () => {
      console.clear();
    });
  }
}

// Disable cache in dev unless using ?cache
// Want to make sure we are testing loading states
if (DEV && !cacheFlag) {
  const response = await cache.match(cacheKey);
  const json = (await response?.json<[string, unknown][]>()) ?? [];
  const arr = json.filter(([key]) => key === '/api/user/me' || key.startsWith('/api/menu/'));
  await cache.put(cacheKey, new Response(JSON.stringify(arr)));
}

// Remove entire cache
if (clearCache || gup('login') === 'success') {
  await cache.delete(cacheKey);
}

// When initializing, we restore the data from the Cache Storage into a map.
const json = (await (await cache.match(cacheKey))?.json<[string, unknown][]>()) ?? [];
const map = new Map<string, unknown>(Array.isArray(json) ? json : []);

const createCacheProvider = () => {
  if (DEV) window.cache = map;

  let unloaded = false;
  const saveCache = () => {
    if (unloaded) return;
    unloaded = true;
    if (!gapi.client.getToken()) return;

    for (const [key, value] of map.entries()) {
      const val = get<{ data: ExportResult }>(value);
      const data = val?.data;
      const url = data?.url;
      // Don't store blob urls in cache
      if (val && url?.startsWith('blob:')) {
        map.set(key, { ...val, data: { ...data, url: undefined } });
      }
      // Don't persist menu state
      if (key.startsWith('/api/menu/')) {
        map.delete(key);
      }
    }

    // Debugging: expired token
    if (DEV && expiredToken) {
      const user = map.get('/api/user/me');
      const val = get<{ data: Profile }>(user);
      if (val) {
        map.set('/api/user/me', {
          ...val,
          data: {
            ...val.data,
            tokens: {
              access_token: 'expired',
              expires: 0,
              expiresAt: Date.now(),
            },
          },
        });
      }
    }

    const appCache = JSON.stringify(Array.from(map.entries()));
    void cache.put(cacheKey, new Response(appCache));
  };

  // https://github.com/segmentio/analytics-next/blob/master/packages/browser/src/lib/on-page-change.ts
  if (isIOS) {
    window.addEventListener('pagehide', saveCache);
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') saveCache();
      else unloaded = false;
    });
  }
  window.addEventListener('beforeunload', saveCache);
  return () => map as Cache;
};

const cacheProvider = typeof window.caches === 'undefined' ? undefined : createCacheProvider();
type Err =
  | { result: { error: { message: string; code: number } } }
  | {
      body: string;
      result: false;
      status: 403;
      statusText: null;
    };
const config: SWRConfiguration = {
  ...(cacheProvider && { provider: cacheProvider }),
  compare: dequal,
  revalidateOnMount: true,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  ...(DEV && { use: [failRequests] }),
  // This needs to be async, so error can throw for 404's
  onError: async (err: Err, key) => {
    log.info('Request error for path:', key);
    if (import.meta.env.DEV) {
      // if ('status' in err && err.status === 403) {
      //   throw {
      //     message: dedent /* json */`Request error:
      //       {
      //         key: "${key}"
      //         code: ${err.status},
      //         message: "${cleanHTML(err.body)}",
      //       }`,
      //   };
      // }
      // if (err.result) {
      //   if (err.result.error.code === 401) {
      //     log.info('Token expired, refreshing user');
      //     const response = map.get('/api/user/me') as { data: Profile } | undefined;
      //     if (response?.data) {
      //       // accessToken permissions error
      //       const response = await fetch('/api/user/me', { priority: 'high' });
      //       if (!response.ok) throw new Error('Error fetching user');
      //       const user = await response.json<Profile | undefined>();
      //       if (!user) return undefined;
      //       map.set('/api/user/me', {
      //         ...response,
      //         data: user,
      //       });
      //     }
      //   }
      //   throw {
      //     message: dedent /* json */`Request error:
      //       {
      //         key: "${key}"
      //         code: ${err.result.error.code},
      //         message: "${err.result.error.message}",
      //       }`,
      //   };
      // }
      if ('message' in err && typeof err.message === 'string') {
        if (err.message.startsWith('Failed to fetch file: Not Found')) return;
        throw err;
      }
    }
  },
};

const staticTreeName = 'wc-static-tree';
const staticTreeWC = (props: { files: DriveFile[]; wiki: DriveFile }) => (
  <div className={lightTheme} data-theme="light">
    <StaticTree {...props} />
  </div>
);

if (!customElements.get(staticTreeName)) {
  customElements.define(
    staticTreeName,
    r2wc(staticTreeWC, {
      props: { files: 'json', wiki: 'json' },
    })
  );
}

// web components for legacy
if (LEGACY) {
  type Props = Parameters<typeof Search>[0];

  const SearchWC = (props: Props) => (
    <SWRConfig value={config}>
      <div className={lightTheme} data-theme="light">
        <Search {...props} />
      </div>
    </SWRConfig>
  );
  const searchComponentName = 'wc-search';
  if (!customElements.get(searchComponentName)) {
    customElements.define(
      searchComponentName,
      r2wc(SearchWC, {
        props: {
          files: 'json',
          wiki: 'json',
        },
      })
    );
  }

  const titleComponentName = 'wc-title';
  const TitleWC = (props: { title: string }) => (
    <div className={lightTheme} data-theme="light">
      <div className={wcStyles.titleContainer}>
        <Title className={wcStyles.title}>{props.title}</Title>
      </div>
    </div>
  );
  if (!customElements.get(titleComponentName)) {
    customElements.define(titleComponentName, r2wc(TitleWC, { props: { title: 'string' } }));
  }

  const lastModifiedName = 'wc-last-modified';
  const LastModifiedWC = ({ content, file }: { content?: string; file: DriveFile }) => (
    <div className={lightTheme} data-theme="light">
      <LastModified file={file} content={content} />
    </div>
  );
  if (!customElements.get(lastModifiedName)) {
    customElements.define(lastModifiedName, r2wc(LastModifiedWC, { props: { content: 'string', file: 'json' } }));
  }

  const breadcrumbsName = 'wc-breadcrumbs';
  const BreadcrumbsWC = ({ file, files, wiki }: { file: DriveFile; files: DriveFile[]; wiki: DriveFile }) => (
    <div className={lightTheme} data-theme="light">
      <Breadcrumbs files={files} file={file} wiki={wiki} />
    </div>
  );
  if (!customElements.get(breadcrumbsName)) {
    customElements.define(
      breadcrumbsName,
      r2wc(BreadcrumbsWC, { props: { file: 'json', files: 'json', wiki: 'json' } })
    );
  }

  const prevNextName = 'wc-prev-next';
  const PrevNextWC = ({ file, files, wiki }: { file: DriveFile; files: DriveFile[]; wiki: DriveFile }) => (
    <SWRConfig value={config}>
      <div className={lightTheme} data-theme="light">
        <PrevNext file={file} files={files} wiki={wiki} />
      </div>
    </SWRConfig>
  );
  if (!customElements.get(prevNextName)) {
    customElements.define(prevNextName, r2wc(PrevNextWC, { props: { file: 'json', files: 'json', wiki: 'json' } }));
  }

  const wikiSelectorName = 'wc-wiki-selector';
  const WikiSelectorWC = ({
    wiki,
    wikis,
    user,
  }: {
    wiki: DriveFile;
    wikis: DriveFile[] | undefined;
    user: Profile | undefined;
  }) => (
    <div className={lightTheme} data-theme="light">
      <WikiSelector wiki={wiki} wikis={wikis} user={user} />
    </div>
  );
  if (!customElements.get(wikiSelectorName)) {
    customElements.define(
      wikiSelectorName,
      r2wc(WikiSelectorWC, { props: { wiki: 'json', wikis: 'json', user: 'json' } })
    );
  }

  const poweredByName = 'wc-powered-by';
  const PoweredByWC = () => (
    <div className={lightTheme} data-theme="light">
      <PoweredBy />
    </div>
  );

  if (!customElements.get(poweredByName)) {
    customElements.define(poweredByName, r2wc(PoweredByWC, {}));
  }

  const iconName = 'wc-icon';
  const IconWC = (props: {
    file: DriveFile;
    color: string;
    hasChildren: boolean;
    sidebar: boolean;
    bgColor?: string;
  }) => {
    return (
      <div className={lightTheme} data-theme="light">
        <Icon {...props} />
      </div>
    );
  };
  if (!customElements.get(iconName)) {
    customElements.define(
      iconName,
      r2wc(IconWC, {
        props: { file: 'json', color: 'string', hasChildren: 'boolean', sidebar: 'boolean', bgColor: 'string' },
      })
    );
  }

  // const pageName = 'wc-page';
  // const PageWC = (props: PropsOf<typeof Page>) => (
  //   <div className={lightTheme} data-theme="light">
  //     <Page {...props} />
  //   </div>
  // );
  // if (customElements.get(pageName)) {
  //   customElements.define(
  //     pageName,
  //     r2wc(PageWC, {
  //       props: {
  //         content: 'string',
  //         height: 'string',
  //         file: 'json',
  //         indexPage: 'json',
  //         isLoading: 'boolean',
  //         url: 'string',
  //       },
  //     })
  //   );
  // }
}

if (MODERN) {
  const root = document.getElementById('root');
  if (!root) throw new Error('Root element not found');

  createRoot(root).render(
    // Disabled: https://github.com/facebook/react/issues/29915
    // <StrictMode>
    <MuiThemeWrapper>
      <SWRConfig value={config}>
        <Switch>
          <Route path="*" component={AppLoader} />
        </Switch>
      </SWRConfig>
    </MuiThemeWrapper>
  );
  // </StrictMode>
}
